<script setup>
import { reactive, ref, computed, watch } from 'vue'
import http from '@/api/http'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import RadioButton from 'primevue/radiobutton'
import Select from 'primevue/select'
import Message from 'primevue/message'
import Tag from 'primevue/tag'
import ToggleSwitch from 'primevue/toggleswitch'
import DatePicker from 'primevue/datepicker'

const scopeOptions = [
  { label: 'Только актуальные перечни', value: 'actual' },
  { label: 'Все перечни (включая историю)', value: 'all' },
]

const form = reactive({
  external_id: '',
  full_name: '',
  birth_date: null,
  birth_year: '',
  inn: '',
  snils: '',
  scope: 'all',
  fuzzy_fio: false,
})

const submitting = ref(false)
const error = ref('')
const result = ref(null)

const inputCount = computed(() => {
  let n = 0
  if (form.full_name.trim()) n++
  if (form.inn.trim()) n++
  if (form.snils.trim()) n++
  return n
})

const formValid = computed(() => {
  if (form.inn.trim() || form.snils.trim()) return true
  if (form.full_name.trim() && (form.birth_date || form.birth_year)) return true
  return false
})

function hasBirthForFuzzy() {
  return (
    !!form.birth_date ||
    /^\d{4}$/.test(String(form.birth_year ?? '').trim())
  )
}

watch(
  () => [form.birth_date, form.birth_year],
  () => {
    if (!hasBirthForFuzzy()) {
      form.fuzzy_fio = false
    }
  },
)

function reset() {
  form.external_id = ''
  form.full_name = ''
  form.birth_date = null
  form.birth_year = ''
  form.inn = ''
  form.snils = ''
  form.fuzzy_fio = false
  result.value = null
  error.value = ''
}

function fmtBirthDate(d) {
  if (!d) return null
  if (typeof d === 'string') return d
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

async function submit() {
  error.value = ''
  result.value = null
  if (!formValid.value) {
    error.value = 'Заполните ИНН/СНИЛС или ФИО + дата/год рождения'
    return
  }

  const subject = {}
  if (form.external_id.trim()) subject.external_id = form.external_id.trim()
  if (form.full_name.trim()) subject.full_name = form.full_name.trim()
  if (form.inn.trim()) subject.inn = form.inn.trim()
  if (form.snils.trim()) subject.snils = form.snils.trim()
  const bd = fmtBirthDate(form.birth_date)
  if (bd) subject.birth_date = bd
  if (form.birth_year && /^\d{4}$/.test(String(form.birth_year))) {
    subject.birth_year = Number(form.birth_year)
  }

  submitting.value = true
  try {
    const { data } = await http.post('/admin/check', {
      scope: form.scope,
      fuzzy_fio: form.fuzzy_fio,
      subjects: [subject],
    })
    result.value = data?.results?.[0] ?? null
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Ошибка проверки'
  } finally {
    submitting.value = false
  }
}

function listLabel(t) {
  return {
    rfm: 'Росфинмониторинг',
    un_sc: 'СБ ООН',
    mvk: 'МВК',
  }[t] ?? t
}

function fmtCalendarRu(s) {
  if (!s) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s))
  if (m) return `${m[3]}.${m[2]}.${m[1]}`
  return String(s)
}

function fmtPublishedUtc(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('ru-RU', { timeZone: 'UTC' })
  } catch (_) {
    return iso
  }
}
</script>

<template>
  <div class="page check">
    <div class="grid">
      <form class="card form" @submit.prevent="submit">
        <h3 class="card__title">Параметры проверки</h3>

        <div class="row">
          <label for="externalId">Внутренний ИД</label>
          <InputText
            id="externalId"
            v-model="form.external_id"
            placeholder="например, ID клиента в вашей CRM"
            maxlength="191"
            fluid
          />
          <small class="muted">
            Не участвует в поиске. Сохраняется в журнале и помогает собирать историю проверок
            одного и того же лица при смене ФИО или паспортных данных.
          </small>
        </div>

        <div class="row">
          <label>ФИО</label>
          <InputText
            v-model="form.full_name"
            placeholder="Иванов Иван Иванович"
            fluid
          />
          <small class="muted">Можно «Ё/Е», запятые и точки — нормализуются автоматически.</small>
        </div>

        <div class="row two">
          <div>
            <label>Дата рождения</label>
            <DatePicker
              v-model="form.birth_date"
              date-format="dd.mm.yy"
              show-icon
              fluid
              show-button-bar
              append-to="body"
              placeholder="дд.мм.гггг"
            />
          </div>
          <div>
            <label>Или год рождения</label>
            <InputText
              v-model="form.birth_year"
              placeholder="1980"
              maxlength="4"
              :disabled="!!form.birth_date"
              fluid
            />
          </div>
        </div>

        <div class="row two">
          <div>
            <label>ИНН</label>
            <InputText v-model="form.inn" placeholder="—" fluid />
          </div>
          <div>
            <label>СНИЛС</label>
            <InputText v-model="form.snils" placeholder="—" fluid />
          </div>
        </div>

        <div class="row">
          <label>Где искать</label>
          <div class="scopes">
            <div v-for="opt in scopeOptions" :key="opt.value" class="scope">
              <RadioButton
                :input-id="`scope-${opt.value}`"
                v-model="form.scope"
                :value="opt.value"
              />
              <label :for="`scope-${opt.value}`">{{ opt.label }}</label>
            </div>
          </div>
        </div>

        <div class="row toggle-row" v-if="form.full_name.trim()">
          <div class="toggle-line">
            <ToggleSwitch
              v-model="form.fuzzy_fio"
              input-id="fuzzyFio"
              :disabled="!hasBirthForFuzzy()"
            />
            <label for="fuzzyFio" class="toggle-label">Нечёткий поиск по ФИО</label>
          </div>
          <small class="muted">
            Дополнительно: совпадение по частям ФИО (LIKE) и полнотекстовый индекс; латиница BGN ↔ кириллица (ICU).
            Для ИНН/СНИЛС не используется.
          </small>
          <small
            v-if="!hasBirthForFuzzy()"
            class="muted fuzzy-hint"
          >
            Включится после указания даты или года рождения (для поиска по ФИО они обязательны).
          </small>
        </div>

        <Message v-if="error" severity="error" :closable="false">{{ error }}</Message>

        <div class="actions">
          <Button
            label="Сбросить"
            severity="secondary"
            text
            icon="pi pi-times"
            @click="reset"
            :disabled="submitting"
          />
          <Button
            type="submit"
            label="Проверить"
            icon="pi pi-search"
            :loading="submitting"
            :disabled="!formValid"
          />
        </div>
      </form>

      <div class="card result">
        <h3 class="card__title">Результат</h3>

        <p v-if="!result && !submitting" class="muted">Заполните форму и нажмите «Проверить».</p>
        <p v-else-if="submitting" class="muted">Идёт поиск…</p>

        <template v-else-if="result">
          <div class="result__head">
            <div class="result__head-info">
              <div class="result__name">
                {{ result.input.full_name || result.input.inn || result.input.snils || '—' }}
              </div>
              <div class="muted">
                <span v-if="result.input.external_id">внутр. ИД: {{ result.input.external_id }}</span>
                <span v-if="result.input.birth_date">
                  <template v-if="result.input.external_id"> · </template>ДР: {{ result.input.birth_date }}
                </span>
                <span v-else-if="result.input.birth_year">
                  <template v-if="result.input.external_id"> · </template>{{ result.input.birth_year }} г.р.
                </span>
                <span v-if="result.input.inn"> · ИНН: {{ result.input.inn }}</span>
                <span v-if="result.input.snils"> · СНИЛС: {{ result.input.snils }}</span>
              </div>
            </div>
            <Tag
              :severity="result.actual?.found ? 'danger' : 'success'"
              :value="result.actual?.found ? 'НАЙДЕН в актуальных' : 'не найден в актуальных'"
            />
          </div>

          <div v-if="result.history" class="result__history muted">
            История: {{ result.history.found ? `${result.history.matches_count} совпадений` : 'нет' }}
          </div>

          <div v-if="result.error === 'no_criteria'" class="muted error">
            Слишком мало данных для поиска (нужно ИНН/СНИЛС или ФИО + дата/год).
          </div>

          <div v-if="result.actual?.matches?.length" class="matches">
            <h4 class="matches__title">Совпадения в актуальных перечнях</h4>
            <div
              v-for="(m, idx) in result.actual.matches"
              :key="idx"
              class="match"
            >
              <div class="match__head">
                <Tag :value="listLabel(m.list_type)" severity="info" />
                <Tag v-if="m.is_terrorist" value="террор" severity="danger" />
                <span class="muted match__scroll-meta">
                  дата перечня {{ fmtCalendarRu(m.scroll_date) }}
                  <template v-if="m.scroll_list_published_at">
                    · опубликован {{ fmtPublishedUtc(m.scroll_list_published_at) }} UTC
                  </template>
                  <template v-if="m.scroll_previous_list_date">
                    · предыдущий перечень {{ fmtCalendarRu(m.scroll_previous_list_date) }}
                  </template>
                </span>
              </div>
              <div class="match__body">
                <div><strong>{{ m.subject.full_name }}</strong></div>
                <div class="muted">
                  <span v-if="m.subject.birth_date">ДР: {{ m.subject.birth_date }}</span>
                  <span v-if="m.subject.inn"> · ИНН: {{ m.subject.inn }}</span>
                  <span v-if="m.subject.snils"> · СНИЛС: {{ m.subject.snils }}</span>
                </div>
                <div class="muted">
                  Тип: {{ m.subject.subject_type === 'person' ? 'физлицо' : 'юрлицо' }}
                  <span v-if="m.list_subtype"> · подсписок: {{ m.list_subtype }}</span>
                  <span v-if="m.subject_id"> · subject_id: {{ m.subject_id }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: minmax(320px, 420px) 1fr;
  gap: 1rem;
}

@media (max-width: 900px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.card {
  background: #fff;
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
}

.card__title {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.row > label {
  font-size: 0.85rem;
  color: #4b5563;
}

.row.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.scopes {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.scope {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.toggle-row {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.toggle-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toggle-label {
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
}

.fuzzy-hint {
  font-size: 0.8rem;
}

.row.two :deep(.p-datepicker) {
  width: 100%;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.error {
  color: #b91c1c;
}

.result__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
}

.result__name {
  font-size: 1.05rem;
  font-weight: 600;
}

.result__history {
  margin-bottom: 1rem;
}

.matches {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.matches__title {
  margin: 0.5rem 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
}

.match {
  border: 1px solid var(--p-red-200, #fecaca);
  background: #fff5f5;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
}

.match__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.match__body > div + div {
  margin-top: 0.125rem;
}

.match__scroll-meta {
  flex: 1;
  min-width: 12rem;
}
</style>
